<template>
  <div @click="onBackgroundClick">
    <b-container fluid class="top-shelf shadow-sm">
      <b-row align-h="center" class="my-3">
        <b-col lg="6" sm="8">
          <TitleBadge v-if="ready" type="Project" :title="project.name">
            <template #id>{{ project | projectId }}</template>
          </TitleBadge>
        </b-col>
        <b-col lg="2" sm="3" class="text-right">
        </b-col>
      </b-row>
    </b-container>
    <b-container fluid>
      <b-row align-h="center" class="pt-3">
        <b-col lg="8" sm="9" cols="11">
          <b-row>
            <b-col lg="4" cols="12" class="text-truncate mb-3 mb-lg-0">
              <p class="font-lato text-muted mb-1 fs-15">
                Cliente
              </p>
              <b-link v-if="ready && project.client" :to="`/app/prod/clientes/${project.client.id}`" class="related-link">
                <span class="fs-14">{{ project.client.name }}</span>
              </b-link>
            </b-col>
            <b-col lg="2" offset-lg="4" cols="6">
              <p class="font-lato text-muted mb-1 fs-15">
                Asesor
              </p>
              <p v-if="ready && project.user" class="mb-0 fs-14">
                {{ project.user.first_name }} {{ project.user.last_name }}
              </p>
            </b-col>
            <b-col lg="2" cols="6" class="text-right">
              <p class="font-lato mb-1 fs-15" style="color: #6c757d">
                Status
              </p>
              <p v-if="ready" class="text-warning fs-14">
                {{ project.status.toUpperCase() }}
              </p>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
    <hr>

    <b-container fluid>
      <b-row align-h="center" class="mx-0 mb-2">
        <b-col xl="10" lg="8">
          <b-row align-h="end" class="mx-0 pr-2 mb-3 mb-sm-0">
            <b-button-group v-if="ready">
              <Button v-if="isSupervisor || currentUser.id === project.user_id" size="sm" @click="openProjectEditModal">
                <font-awesome-icon :icon="['far', 'edit']" fixed-width />
                <span class="pl-1">Editar</span>
              </Button>
              <Button v-if="isSupervisor" size="sm" @click="transferModalShow = true">
                <font-awesome-icon :icon="['far', 'share']" fixed-width />
                <span class="pl-1">Transferir</span>
              </Button>
            </b-button-group>
          </b-row>
          <b-row class="fs-13">
            <b-col lg="5" offset-xl="1">
              <table class="table table-sm table-borderless">
                <tbody>
                  <tr v-if="ready">
                    <td class="text-muted" style="width: 150px">
                      <font-awesome-icon  v-if="project.project_type === 'Comercial'" :icon="['fas', 'industry-alt']" fixed-width />
                      <font-awesome-icon v-else-if="project.project_type == 'Residencial'" :icon="['fas', 'home']" fixed-width />
                      <font-awesome-icon v-else :icon="['fas', 'parking']" fixed-width />
                      <span>Tipo:</span>
                    </td>
                    <td v-if="ready">{{ project.project_type }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">
                      <font-awesome-icon :icon="['fas', 'map-marker-alt']" fixed-width />
                      <span>Dirección:</span>
                    </td>
                    <td v-if="ready">{{ project.address }}</td>
                  </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>

          <!-- Tabs for Quotes, Accesses, CRM (mobile) -->
          <b-row class="mr-0 mr-sm-2 mt-5">
            <b-card no-body style="width: 100%; min-height: 150px">
              <b-tabs card v-model="tabIndex" active-nav-item-class="font-weight-bold">
                <!-- QUOTES TAB -->
                <b-tab title-link-class="font-lato-tab">
                  <template #title>
                    Cotizaciones ({{ quoteList.length }})
                  </template>
                  <ProjectShowQuoteList
                    v-if="ready"
                    :project="project"
                    :quoteData="quoteData"
                    :quoteList="quoteList"
                    @quote-created="createQuote"
                    @quote-updated="updateQuote"
                  />
                  <div v-else class="text-center text-info mt-3">
                    <b-spinner small type="grow"></b-spinner>
                    <span class="pl-2 fs-14">Cargando datos...</span>
                  </div>
                </b-tab>
                <!-- Submital TAB -->
                <b-tab title-link-class="font-lato-tab">
                  <template #title>
                    Submitals ({{ accesses.length + submitalList.length }})
                  </template>
                  <ProjectShowSubmitalList
                    v-if="ready"
                    ref="accessList"
                    :project="project"
                    :accessData="accessData"
                    :accessList="accessList"
                    :submitalData="submitalData"
                    :submitalList="submitalList"
                    :designs="accessDesigns"
                    :motors="accessMotors"
                    @access-created="createAccess"
                    @access-updated="updateAccess"
                    @submital-created="createSubmital"
                    @submital-updated="updateSubmital"
                    @access-control-created="createAccessControl"
                  />
                </b-tab>
                <b-tab title-link-class="font-lato-tab">
                  <template #title>
                    Submitals CA ({{accessControlList.length}})
                  </template>
                  <ProjectAccessControlList
                    :project="project"
                    :submitalData="accessControlData"
                    :submitalList="accessControlList"
                    @submital-created="createAccessControl"
                    @submital-updated="updateAccessControl"
                    @display-alert="displayAlert"
                  />
                </b-tab>
                <!-- Drawing Requests Tab -->
                <b-tab title-link-class="font-lato-tab">
                  <template #title>
                    Planos ({{ drawingRequestList.length }})
                  </template>
                  <DrawingRequestsTab
                    v-if="ready"
                    :project="project"
                    :drawing-requests="drawingRequests"
                    @drawing-request-created="createDrawingRequest"
                  />
                  <div v-else class="text-center text-info mt-3">
                    <b-spinner small type="grow"></b-spinner>
                    <span class="pl-2 fs-14">Cargando datos...</span>
                  </div>
                </b-tab>
              </b-tabs>
            </b-card>
          </b-row>
        </b-col>
      </b-row>
    </b-container>

    <!-- Edit Project Modal -->
    <Modal
      v-if="editProjectModalShow"
      contentWidth="600px"
      :required=true
      @close="editProjectModalShow = false"
    >
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Editar proyecto
        </p>
      </template>
      <template #body>
        <b-row align-h="center">
          <b-col>
            <ValidationObserver v-slot="{ invalid }">
              <ValidationProvider rules="required" name="Nombre" v-slot="{ valid, errors }">
                <b-form-group :invalid-feedback="errors[0]" >
                  <template #label>
                    <span>Nombre</span><span class="asterisk">*</span>
                  </template>
                  <b-input v-model="projectForm.name" :state="errors[0] ? false : (valid ? true : null)" />
                </b-form-group>
              </ValidationProvider>

              <b-form-group label="Tipo de ciclos de proyecto" >
                <b-radio-group v-model="projectForm.project_type" stacked>
                  <b-radio value="Comercial">Comercial o Industrial</b-radio>
                  <b-radio value="Residencial">Residencial o Condominio</b-radio>
                  <b-radio value="Parqueo">Parqueo</b-radio>
                </b-radio-group>
              </b-form-group>

              <ValidationProvider rules="required" name="Dirección" v-slot="{ valid, errors }">
                <b-form-group :invalid-feedback="errors[0]" >
                  <template #label>
                    <span>Dirección</span><span class="asterisk">*</span>
                  </template>
                  <b-textarea v-model="projectForm.address" rows="3" :state="errors[0] ? false : (valid ? true : null)" />
                </b-form-group>
              </ValidationProvider>

              <b-form-group label="Status">
                <b-radio-group v-model="projectForm.status" :options="statusOptions" stacked></b-radio-group>
              </b-form-group>
              <div class="text-info" >
                <small v-if="projectForm.status === 'baja'">Baja probabilidad (menos de 50%)</small>
                <small v-if="projectForm.status === 'media'">Media probabilidad (50 a 79%)</small>
                <small v-if="projectForm.status === 'alta'">Alta probabilidad (más de 80%)</small>
                <small v-if="projectForm.status === 'vendido'">Quedo abierta pero no se jalaron todas las líneas - CERRAR</small>
                <small v-if="projectForm.status === 'desestimado'">El cliente no va a hacer nada (ni con nosotros, ni con nadie) - CERRAR</small>
                <small v-if="projectForm.status === 'perdido'">El trabajo lo hizo otra empresa o persona.  Puede ser por precio, tiempo de respuesta de la gestión, especificaciones, plazo de entrega del producto, falta de tiempo del asesor, falla en la atención/servicio - CERRAR</small>
              </div>

              <div class="text-center mt-4 mb-2">
                <Button variant="green" :disabled="invalid" @click="updateProject">Guardar</Button>
              </div>
            </ValidationObserver>
          </b-col>
        </b-row>
      </template>
      <template #footer>
        <span></span>
      </template>
    </Modal>

    <!-- Transfer Project Modal -->
    <Modal
      v-if="transferModalShow"
      contentWidth="600px"
      :required=true
      @close="transferModalShow = false"
    >
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Transferir este proyecto a otro asesor
        </p>
      </template>
      <template #body>
      <b-row align-h="center">
        <b-col>
          <b-select v-model="transfereeId">
            <template #first>
              <option :value="null" disabled>- Eligir otro asesor -</option>
            </template>
            <option v-for="employee in eligibleTransferees" :value="employee.id" :key="employee.id">{{ employee | concatName4 }}</option>
          </b-select>
        </b-col>
      </b-row>
      </template>
      <template #footer>
        <Button variant="green" :disabled="!transfereeId" @click="transfer">
          Transferir
        </Button>
      </template>
    </Modal>

    <!-- Alert -->
    <ToastAlert :show="alertShow" :variant="alertVariant" @close="alertShow = false">
      {{ alertMessage }}
    </ToastAlert>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { normalize, schema } from 'normalizr'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import Button from '../../shared/Button'
import TitleBadge from '../../shared/TitleBadge'
import ToastAlert from '../../shared/ToastAlert'
import ProjectShowSubmitalList from './ProjectShowSubmitalList'
import ProjectShowQuoteList from './ProjectShowQuoteList'
import ProjectAccessControlList from './ProjectAccessControlList'
import Modal from '../../shared/Modal'
import DrawingRequestsTab from './DrawingRequestsTab'

export default {
  name: 'ProjectShow',
  components: {
    ValidationObserver,
    ValidationProvider,
    Button,
    TitleBadge,
    ToastAlert,
    ProjectShowSubmitalList,
    ProjectShowQuoteList,
    ProjectAccessControlList,
    Modal,
    DrawingRequestsTab
  },
  data () {
    return {
      id: Number(this.$route.params.id),
      project: null,
      quoteData: {},
      quoteList: [],
      accessData: {},
      accessList: [],
      submitalData: {},
      submitalList: [],
      accessControlData: {},
      accessControlList: [],
      accessDesigns: [],
      accessMotors: [],
      editProjectModalShow: false,
      projectForm: {
        name: null,
        project_type: null,
        address: null,
        status: null
      },
      transferModalShow: false,
      transfereeId: null,
      tabIndex: 0,  // change according to project stage
      statusOptions: [
        { text: 'Baja probabilidad', value: 'baja' },
        { text: 'Media probabilidad', value: 'media' },
        { text: 'Alta probabilidad', value: 'alta' },
        { text: 'Vendido', value: 'vendido' },
        { text: 'Desestimado', value: 'desestimado' },
        { text: 'Perdido', value: 'perdido' },
        { text: 'Licitación Construcción', value: 'licitación' }
      ],
      ready: false,
      processing: false,
      alertShow: false,
      alertVariant: null,
      alertMessage: '',
      drawingRequestData: {},
      drawingRequestList: []
    }
  },
  mounted () {
    this.fetchProjectData()
  },
  computed: {
    ...mapGetters (['isSupervisor', 'salespeople', 'salesSupervisors', 'employeeDataLoaded']),
    currentUser () { return this.$store.state.currentUser },
    eligibleTransferees () {
      if (this.project) { return this.salespeople.concat(this.salesSupervisors).filter(s => s.id !== this.project.user_id) }
    },
    accesses () { // for Tab counter
      return this.accessList.map(id => this.accessData[id]).filter(access => access.access_no.substring(0,2) != 'CT')
    },
    drawingRequests () {
      return this.drawingRequestList.map(id => this.drawingRequestData[id])
    }
  },
  methods: {
    fetchProjectData () {
      this.ready = false
      this.$http.get(`/api/production/projects/${this.id}`)
        .then((res) => {
          // console.log('fetching project data:')
          // console.log(res.data)
          this.project = res.data.project

          this.formatQuotes(res.data.quotes)
          this.formatSubmitals(res.data)
          this.formatDrawingRequests(res.data.drawing_requests)

          if (this.accessList.length > 0) {
            this.tabIndex = 1
          }
          this.ready = true
        })
        .catch(e => console.log(e))
        .finally(() => this.fetchDesignsMotors())
    },
    formatQuotes (quotes) {
      const quoteData = normalize(
        { quotes: quotes },
        { quotes: [ new schema.Entity('quotes') ] }
      )
      if (quoteData.entities.hasOwnProperty('quotes')) {
        this.quoteData = quoteData.entities.quotes
      }
      this.quoteList = quoteData.result.quotes
    },
    formatSubmitals (response) {
      // console.log(response);
      // accesses
      const accessData = normalize(
        { accesses: response.accesses },
        { accesses: [ new schema.Entity('accesses') ] }
      )
      if (accessData.entities.hasOwnProperty('accesses')) {
        this.accessData = accessData.entities.accesses
      }
      this.accessList = accessData.result.accesses

      // submitals without CA
      const filteredSubmitals = response.submitals.filter(s => s.configurable_type != 'Configurator::ControlAcceso')
      const submitalData = normalize(
        { submitals: filteredSubmitals },
        { submitals: [ new schema.Entity('submitals') ] }
      )
      if (submitalData.entities.hasOwnProperty('submitals')) {
        this.submitalData = submitalData.entities.submitals
      }
      this.submitalList = submitalData.result.submitals

      // accesses with CA
      const caSubmitals = response.submitals.filter(s => s.configurable_type == 'Configurator::ControlAcceso')
      const caSubmitalData = normalize(
        { submitals: caSubmitals },
        { submitals: [ new schema.Entity('submitals') ] }
      )
      if (caSubmitalData.entities.hasOwnProperty('submitals')) {
        this.accessControlData = caSubmitalData.entities.submitals
      }
      this.accessControlList = caSubmitalData.result.submitals
    },
    formatDrawingRequests (drawingRequests) {
      const drawingRequestData = normalize(
        { drawing_requests: drawingRequests },
        { drawing_requests: [ new schema.Entity('drawing_requests') ] }
      )
      if (drawingRequestData.entities.hasOwnProperty('drawing_requests')) {
        this.drawingRequestData = drawingRequestData.entities.drawing_requests
      }
      this.drawingRequestList = drawingRequestData.result.drawing_requests
    },
    fetchDesignsMotors () {
      this.$http.get(`/api/production/projects/fetch_designs_motors`)
        .then((res) => {
          // console.log(res.data)
          this.accessDesigns = res.data.designs
          this.accessMotors = res.data.motors
        })
    },
    openProjectEditModal () {
      this.projectForm.name = this.project.name
      this.projectForm.project_type = this.project.project_type
      this.projectForm.address = this.project.address
      this.projectForm.status = this.project.status
      this.editProjectModalShow = true
    },
    updateProject () {
      if (this.processing) { return }
      this.processing = true
      this.editProjectModalShow = false
      this.$http.put(`/api/production/projects/${this.id}`, {
          project: this.projectForm
        })
        .then((res) => {
          // console.log(res)
          this.project = res.data
          this.alertVariant = 'success'
          this.alertMessage = 'Cambios se guardaron'
        })
        .catch(error => {
          console.log(error)
          this.alertVariant = 'danger'
          if (error.response.data.errors) {
            this.alertMessage = error.response.data.errors[0]
          } else {
            this.alertMessage = 'No se pudo guardar los cambios'
          }
        })
        .finally(() => {
          this.processing = false
          this.alertShow = true
        })
    },
    transfer () {
      if (this.processing) { return }
      this.processing = true
      this.$http.put(`/api/production/projects/${this.id}/transfer`, {
          project: { user_id: this.transfereeId }
        })
        .then(response => {
          // console.log(response)
          let user = response.data
          this.project.user = user
          this.project.user_id = user.id
          this.transfereeId = null
          this.alertVariant = 'success'
          this.alertMessage = `Proyecto se ha transferido a ${user.first_name + ' ' + user.last_name}`
        })
        .catch(error => {
          this.alertVariant = 'danger'
          this.alertMessage = 'No funcionó'
        })
        .finally(() => {
          this.processing = false
          this.transferModalShow = false
          this.alertShow = true
        })
    },
    loadSalespeople () {
      if (!this.employeeDataLoaded) {
        this.$store.dispatch('fetchEmployeeData')
      }
    },
    createQuote (quote) {
      this.quoteList.unshift(quote.id)
      this.$set(this.quoteData, quote.id, quote)
    },
    updateQuote (quote) {
      this.$set(this.quoteData, quote.id, quote)
    },
    createAccess (access) {
      this.accessList.unshift(access.id)
      this.$set(this.accessData, access.id, access)
    },
    updateAccess (access) {
      this.$set(this.accessData, access.id, {
        ...this.accessData[access.id],
        ...access
      })
    },
    createSubmital (submital) {
      this.submitalList.unshift(submital.id)
      this.$set(this.submitalData, submital.id, submital)
    },
    updateSubmital (submital) {
      this.$set(this.submitalData, submital.id, submital)
    },
    onBackgroundClick (e) {
      if (!(e.target.tagName === 'A' || (e.target.closest('tr') && e.target.closest('tr').classList.contains('access-list-row')))) {
        this.$refs.accessList?.cancelEditing()
      }
    },
    createAccessControl (submital) {
      this.accessControlList.unshift(submital.id)
      this.$set(this.accessControlData, submital.id, submital)
      this.tabIndex = 2
    },
    updateAccessControl (submital) {
      this.$set(this.accessControlData, submital.id, {
        ...this.accessControlData[submital.id],
        ...submital
      })
    },
    displayAlert ({ message, variant, time=2000 }) {
      this.alertVariant = variant
      this.alertMessage = message
      this.alertShow = true

      setTimeout(() => this.alertShow = false, time)
    },
    createDrawingRequest (drawingRequest) {
      this.drawingRequestList.unshift(drawingRequest.id)
      this.$set(this.drawingRequestData, drawingRequest.id, drawingRequest)
    }
  }
}
</script>

<style scoped>
.project-card-header {
  font-size: 16px;
  color: #aaa;
  text-transform: uppercase;
  font-family: 'Lato', sans-serif;
}
</style>
